import { fetchJson } from './client';
import {
  StoreApiError,
  type LicensesMeResponse,
  type ValidateKeyDetailedResponse,
} from './types';

/**
 * Fetches the caller's license map from alchemie-commerce `licensesMe`.
 * Returns the server's raw response shape for ergonomics — callers can
 * index directly: `(await fetchLicenses()).licenses['PIPHDEMO']`.
 */
export async function fetchLicenses(): Promise<LicensesMeResponse> {
  const data = await fetchJson<LicensesMeResponse>({
    method: 'GET',
    relativePath: '/licensesMe',
    errorContext: 'License fetch',
  });
  if (
    typeof data !== 'object' ||
    data === null ||
    typeof data.licenses !== 'object' ||
    data.licenses === null
  ) {
    throw new StoreApiError('invalid-response', 'License server returned an unexpected shape.');
  }
  return data;
}

/**
 * Validate a license key against `/validateLicense`. Boolean-returning
 * variant for common call sites (login flow, session re-establishment).
 *
 * Industry-standard grace: `past_due` is treated as valid — the UI shows
 * a banner but the app stays unlocked.
 */
export async function validateKey(key: string, productId?: string): Promise<boolean> {
  const result = await validateKeyDetailed(key, productId);
  return result.valid;
}

/**
 * Variant returning the full server response so callers can surface richer
 * UX (status badge, expiry date, rejection reason).
 */
export async function validateKeyDetailed(
  key: string,
  productId?: string
): Promise<ValidateKeyDetailedResponse> {
  const body: Record<string, unknown> = { key };
  if (productId) body.productId = productId;

  try {
    const data = await fetchJson<ValidateKeyDetailedResponse>({
      method: 'POST',
      relativePath: '/validateLicense',
      body,
      requireAuth: false,
      errorContext: 'Validate key',
    });

    const valid = data.valid === true || data.status === 'past_due';
    return { ...data, valid };
  } catch (err) {
    if (err instanceof StoreApiError) {
      return { valid: false, reason: err.code };
    }
    return { valid: false, reason: 'unknown' };
  }
}
